import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";
import BrandsLanding from "./pages/BrandsLanding";
import HowItWorks from "./pages/HowItWorks";
import About from "./pages/About";
import Features from "./pages/Features";
// import Pricing from "./pages/Pricing"; // hidden pending pricing strategy decision
import Auth from "./pages/Auth";
import Confirm from "./pages/Confirm";
import PlatformConfigDashboard from "./pages/PlatformConfigDashboard";
import PlatformConnections from "./pages/PlatformConnections";
import TopNavDashboardLayout from "./components/dashboard/TopNavDashboardLayout";
import TopNavDashboard from "./pages/dashboard/TopNavDashboard";
import { TopNavPlatforms } from "./pages/dashboard/TopNavPlatforms";
import { TopNavFeeds } from "./pages/dashboard/TopNavFeeds";
import { TopNavRules } from "./pages/dashboard/TopNavRules";
import { TopNavSettings } from "./pages/dashboard/TopNavSettings";
import { TopNavAnalytics } from "./pages/dashboard/TopNavAnalytics";
import { Explore } from "./pages/dashboard/Explore";
import SocialListening from "./pages/dashboard/SocialListening";
import ContentModeration from "./pages/dashboard/ContentModeration";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import DataDeletion from "./pages/DataDeletion";
import Onboarding from "./pages/Onboarding";
import Settings from "./pages/dashboard/Settings";
import Profile from "./pages/dashboard/Profile";
import Upgrade from "./pages/dashboard/Upgrade";
import Help from "./pages/dashboard/Help";
import { AIGoalAssistant } from "./pages/dashboard/AIGoalAssistant";
import AILab from "./pages/AILab";
import Tools from "./pages/dashboard/Tools";
import UnifiedFeeds from "./pages/UnifiedFeeds";
import CuratedFeed from "./pages/CuratedFeed";
import SecurityReport from "./pages/SecurityReport";
import AdminDashboard from "./pages/AdminDashboard";
import ResearchDashboard from "./pages/admin/ResearchDashboard";
import AdminBetaManagement from "./pages/AdminBetaManagement";
import AIAssistants from "./pages/AIAssistants";
import OAuthComplete from "./pages/OAuthComplete";
import OAuthCallback from "./pages/OAuthCallback";
import OAuthCallbackInfo from "./pages/OAuthCallbackInfo";
import AuthCallback from "./pages/AuthCallback";
import TestingPage from "./pages/TestingPage";
import CuratorProfile from "./pages/CuratorProfile";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();


function App() {
  
  
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ThemeProvider>
            <TooltipProvider>
              <Toaster />
            <BrowserRouter>
              <ErrorBoundary>
                <Routes>
              {/* TheDigitalFence.com routes */}
              <Route path="/" element={<Index />} />
              <Route path="/brands" element={<BrandsLanding />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/about" element={<About />} />
              <Route path="/features" element={<Features />} />
              {/* <Route path="/pricing" element={<Pricing />} /> */}
               <Route path="/privacy" element={<Privacy />} />
               <Route path="/terms" element={<Terms />} />
               <Route path="/security" element={<SecurityReport />} />
               <Route path="/data-deletion" element={<DataDeletion />} />
               <Route path="/auth" element={<Auth />} />
               <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/oauth-complete" element={<OAuthComplete />} />
        <Route path="/oauth-callback" element={<OAuthCallback />} />
               <Route path="/api/oauth-callback" element={<OAuthCallbackInfo />} />
               <Route path="/confirm" element={<Confirm />} />
               <Route path="/u/:username" element={<CuratorProfile />} />
               <Route path="/onboarding" element={<Onboarding />} />
                    <Route path="/connections" element={<PlatformConnections />} />
                 <Route path="/feeds" element={<Navigate to="/dashboard/feeds" replace />} />
                 {/* Standalone curated feed is removed - only accessible through dashboard */}

                  {/* Admin routes */}
                   <Route path="/admin" element={<AdminDashboard />} />
                   <Route path="/admin/research" element={<ResearchDashboard />} />
                   <Route path="/admin/beta-management" element={<AdminBetaManagement />} />
                   <Route path="/testing" element={<TestingPage />} />
              
               {/* Main Dashboard - Top Navigation Layout */}
                <Route path="/dashboard" element={<TopNavDashboardLayout />}>
                  <Route index element={<TopNavDashboard />} />
                  <Route path="platforms" element={<TopNavPlatforms />} />
                  <Route path="rules" element={<TopNavRules />} />
                  <Route path="feeds" element={<TopNavFeeds />} />
                  <Route path="settings" element={<TopNavSettings />} />
                  <Route path="analytics" element={<TopNavAnalytics />} />
                  <Route path="listening" element={<SocialListening />} />
                  <Route path="tools" element={<Tools />} />
                  <Route path="explore" element={<Explore />} />
                  <Route path="ai-assistants" element={<AIAssistants />} />
                  <Route path="ai-lab" element={<AILab />} />
                  <Route path="moderation" element={<ContentModeration />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="upgrade" element={<Upgrade />} />
                  <Route path="help" element={<Help />} />
                  {/* Legacy route mappings */}
                  <Route path="filtering" element={<TopNavRules />} />
                   <Route path="curated" element={<CuratedFeed />} />
                   <Route path="curated-feed" element={<CuratedFeed />} />
                </Route>
               
               {/* Legacy routes for backwards compatibility */}
                <Route path="/dashboard/overview" element={<Navigate to="/dashboard" replace />} />
              
               {/* Alternative dashboard layout (sidebar version) - for backwards compatibility */}
                <Route path="/dashboard-sidebar" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard-sidebar/*" element={<Navigate to="/dashboard" replace />} />
              
               {/* Legacy routes that redirect to main dashboard */}
                <Route path="/ai-lab" element={<Navigate to="/dashboard/ai-lab" replace />} />
                <Route path="/ai-assistants" element={<Navigate to="/dashboard/ai-assistants" replace />} />
              
              {/* Platform-specific configuration */}
              <Route path="/platform/:platform/dashboard" element={<PlatformConfigDashboard />} />
              
              {/* Catch all - show 404 page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
              </ErrorBoundary>
            </BrowserRouter>
          </TooltipProvider>
            </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
